import { useMutation } from '@apollo/client';
import { useContext, useRef } from 'react';
import { TextInput as RNTextInput } from 'react-native';
import * as z from 'zod';

import { graphql } from '@oui/lib/src/graphql/tada';
import {
  OnboardingVariant,
  ProductVariant,
  ProductVersion,
  TestPatientInputSchema,
} from '@oui/lib/src/types/graphql.generated';

import { AuthScreenContainer } from '../components/AuthScreenContainer';
import { Button } from '../components/Button';
import { ErrorPresenter } from '../components/ErrorPresenter';
import { Label, Text } from '../components/Text';
import { View } from '../components/View';
import { APP_SLUG } from '../constants';
import {
  Controller,
  EmailInputRender,
  PickerInputRender,
  SwitchInputRender,
  TextInputRender,
  useZodForm,
} from '../form';
import { LogoutContext } from '../hooks/useLogout';
import { login } from '../lib/auth';

export const CreateTestUserMutation = graphql(`
  mutation CreateTestUser($email: String!, $password: String!, $testPatient: TestPatientInput!) {
    createTestUser(email: $email, password: $password, testPatient: $testPatient)
  }
`);

const CreateTestUserMutationSchema = z.object({
  email: z.string(),
  password: z.string(),
  testPatient: TestPatientInputSchema,
});

const DEFAULT_TEST_PATIENT = (
  {
    otx207: {
      productSlug: 'AVIVA_ADOLESCENT',
      roleType: 'PATIENT',
      onboardingVariant: OnboardingVariant.INDEPENDENT,
      caringContactsMedium: null,
    },
    'aviva-support': {
      productSlug: 'AVIVA_ADOLESCENT',
      roleType: 'SUPPORTER',
    },
    'oui-aviva': {
      productSlug: 'AVIVA_ADULT',
      roleType: 'PATIENT',
      onboardingVariant: OnboardingVariant.INDEPENDENT,
      caringContactsMedium: null,
    },
    'oui-aviva-staff': {},
    changebite: { productSlug: 'CHANGE_BITE', roleType: 'PATIENT' },
    yst: {},
    ptsd: { productSlug: 'PTSD', roleType: 'PATIENT' },
    DETOX_TEST: {},
  } satisfies Record<typeof APP_SLUG, z.infer<typeof TestPatientInputSchema>>
)[APP_SLUG];

export function CreateTestUser() {
  const { setIsLoggingOut } = useContext(LogoutContext);
  const [createTestUser, { error, loading }] = useMutation(CreateTestUserMutation);

  const { control, handleSubmit } = useZodForm(
    CreateTestUserMutationSchema.merge(
      z.object({
        email: z.string().email(),
        password: z.string().min(8, 'Must be at least 8 characters'),
      }),
    ),
    {
      defaultValues: {
        password: '',
        email: '',
        testPatient: {
          productVersion: ProductVersion.AVIVA_2_0,
          productStatic: false,
          ...DEFAULT_TEST_PATIENT,
        },
      },
    },
  );

  const emailInputRef = useRef<RNTextInput>(null);
  const passwordInputRef = useRef<RNTextInput>(null);

  const request = handleSubmit(
    async (data) => {
      const email = data.email.trim();
      await createTestUser({ variables: { ...data, email } });
      // A bit of a hack so that we can set mock state if needed before the useIsLoggedIn
      // navigates us away from this screen
      setIsLoggingOut(true);
      await login(email, data.password);
      // any data mocking should be done here
      setIsLoggingOut(false);
    },
    (errors) => {
      console.log('errors', errors);
    },
  );

  return (
    <AuthScreenContainer heading={'Create test user'}>
      <View spacing={12} style={{ paddingBottom: 100 }}>
        <Text text="Thank you for testing Aviva. We appreciate your help!" size={15} />
        <Text
          text="To file a bug report at any time, hold on the screen with three fingers until the Tester menu is shown. Select 'ReportError' and describe your issue in as much detail as possible."
          size={15}
        />
        <Label text="Create new user" />
        {error ? <ErrorPresenter error={error} /> : null}
        <Controller
          control={control}
          render={(props) =>
            EmailInputRender(props, {
              'aria-label': 'Email',
              ref: emailInputRef,
              onSubmitEditing: () => passwordInputRef.current?.focus(),
            })
          }
          name="email"
        />
        <Controller
          control={control}
          render={(props) =>
            TextInputRender(props, {
              secureTextEntry: true,
              autoComplete: 'new-password',
              placeholder: 'Password',
              hint: 'Must be at least 8 characters',
              ref: passwordInputRef,
              onSubmitEditing: (e) => void request(e),
              enterKeyHint: 'next',
              testID: 'Signup_passwordInput',
            })
          }
          name="password"
        />
        {APP_SLUG === 'oui-aviva' ? (
          <Controller
            control={control}
            render={(props) =>
              PickerInputRender(props, {
                label: 'User type',
                items: [
                  { label: 'Adult', value: ProductVariant.AVIVA_ADULT },
                  { label: 'Adolescent', value: ProductVariant.AVIVA_ADOLESCENT },
                  { label: 'Military', value: ProductVariant.AVIVA_MILITARY },
                  { label: 'Inpatient', value: ProductVariant.AVIVA_INPATIENT },
                ],
              })
            }
            name="testPatient.productSlug"
          />
        ) : null}
        <View row style={{ justifyContent: 'space-between' }}>
          <Label text="Control group?" />
          <Controller
            control={control}
            render={(props) =>
              SwitchInputRender(props, {
                'aria-label': 'Control',
                showOnOff: true,
              })
            }
            name="testPatient.productStatic"
          />
        </View>

        <Button text="Submit" onPress={request} loading={loading} />
      </View>
    </AuthScreenContainer>
  );
}
